import { useState } from "react";
import { Popover, MenuItem, Menu, IconButton } from "@mui/material";

const SearchBar = ({
  searchTerm,
  handleSearch,
  setSearchTerm,
  handleNoteFilterClick,
  handleClearFilter,
  handleEventFilterClick,
  filterNoteType,
  filterEventType,
  dateRange,
  onDateRangeChange,
}) => {
  const [searchAnchorEl, setSearchAnchorEl] = useState(null);
  const [dateAnchorEl, setDateAnchorEl] = useState(null);
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [tempDateRange, setTempDateRange] = useState({ fromDate: '', toDate: '' });

  const handleSearchClick = (event) => {
    setSearchAnchorEl(event.currentTarget);
  };

  const handleSearchClose = () => {
    setSearchAnchorEl(null);
  };

  const handleDateClick = (event) => {
    // Initialize temp date range with current values
    setTempDateRange({
      fromDate: dateRange?.fromDate || '',
      toDate: dateRange?.toDate || ''
    });
    setDateAnchorEl(event.currentTarget);
  };

  const handleDateClose = () => {
    setDateAnchorEl(null);
  };

  const handleApplyDateFilter = () => {
    if (onDateRangeChange) {
      onDateRangeChange(tempDateRange);
    }
    handleDateClose();
  };

  const handleClearDateRange = () => {
    setTempDateRange({ fromDate: '', toDate: '' });
    if (onDateRangeChange) {
      onDateRangeChange({ fromDate: '', toDate: '' });
    }
    handleDateClose();
  };

  const handleFilterClick = (event) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  const handleFilterItemClick = (filterType) => {
    (handleEventFilterClick || handleNoteFilterClick)(filterType);
    handleFilterClose();
  };

  const handleClearAllFilters = () => {
    handleClearFilter();
    handleFilterClose();
  };

  const isSearchOpen = Boolean(searchAnchorEl);
  const isDateOpen = Boolean(dateAnchorEl);
  const isFilterOpen = Boolean(filterAnchorEl);

  return (
    <div className="flex flex-row justify-end items-end xs:items-end w-full xs:mt-0">
      <div className="flex gap-2">
        {/* Search Toggle Button */}
        <IconButton
          onClick={handleSearchClick}
          sx={{
            backgroundColor: '#1F456E',
            '&:hover': { backgroundColor: '#163552' },
            width: { xs: 44, sm: 56 },
            height: { xs: 44, sm: 56 },
          }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="w-5 h-5 sm:w-7 sm:h-7"
            viewBox="0 0 24 24"
            fill="none"
          >
            <path
              d="M11.5 21a9.5 9.5 0 1 0 0-19 9.5 9.5 0 0 0 0 19ZM22 22l-2-2"
              stroke="#fff"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </IconButton>

        {/* Search Popover */}
        <Popover
          open={isSearchOpen}
          anchorEl={searchAnchorEl}
          onClose={handleSearchClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          sx={{
            '& .MuiPopover-paper': {
              marginTop: '8px',
              borderRadius: '12px',
              boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
            }
          }}
        >
          <div className="bg-white p-3 w-80 sm:w-96">
            <div className="flex items-center space-x-3">
              <input
                type="text"
                placeholder="Search here..."
                className="w-full rounded-xl p-2 outline-none border border-gray-200 focus:border-[#1F456E]"
                value={searchTerm}
                onChange={handleSearch}
                autoFocus
              />
              <button onClick={() => setSearchTerm("")}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M12 22c5.5 0 10-4.5 10-10S17.5 2 12 2 2 6.5 2 12s4.5 10 10 10ZM9.17 14.83l5.66-5.66M14.83 14.83 9.17 9.17"
                    stroke="#101010"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>
          </div>
        </Popover>

        {/* Date Range Toggle Button */}
        {onDateRangeChange && (
          <>
            <IconButton
              onClick={handleDateClick}
              sx={{
                backgroundColor: isDateOpen || (dateRange?.fromDate || dateRange?.toDate) ? '#ED7117' : '#1F456E',
                '&:hover': { 
                  backgroundColor: isDateOpen || (dateRange?.fromDate || dateRange?.toDate) ? '#d66315' : '#163552' 
                },
                width: { xs: 44, sm: 56 },
                height: { xs: 44, sm: 56 },
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-5 h-5 sm:w-7 sm:h-7"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M8 2v3M16 2v3M3.5 9.09h17M21 8.5V17c0 3-1.5 5-5 5H8c-3.5 0-5-2-5-5V8.5c0-3 1.5-5 5-5h8c3.5 0 5 2 5 5Z"
                  stroke="#fff"
                  strokeWidth="1.5"
                  strokeMiterlimit="10"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M15.695 13.7h.009M15.695 16.7h.009M11.995 13.7h.01M11.995 16.7h.01M8.294 13.7h.01M8.294 16.7h.01"
                  stroke="#fff"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </IconButton>

            {/* Date Range Popover */}
            <Popover
              open={isDateOpen}
              anchorEl={dateAnchorEl}
              onClose={handleDateClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              sx={{
                '& .MuiPopover-paper': {
                  marginTop: '8px',
                  borderRadius: '12px',
                  boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
                }
              }}
            >
              <div className="bg-white p-4">
                <div className="flex flex-col gap-3">
                  <div className="flex flex-col">
                    <label className="text-xs text-gray-600 mb-1 font-medium">From Date</label>
                    <input
                      type="date"
                      className="rounded-lg p-2 border border-gray-200 outline-none text-sm focus:border-[#1F456E]"
                      value={tempDateRange.fromDate}
                      onChange={(e) => setTempDateRange({ ...tempDateRange, fromDate: e.target.value })}
                      max={new Date().toISOString().split('T')[0]}
                    />
                  </div>
                  <div className="flex flex-col">
                    <label className="text-xs text-gray-600 mb-1 font-medium">To Date</label>
                    <input
                      type="date"
                      className="rounded-lg p-2 border border-gray-200 outline-none text-sm focus:border-[#1F456E]"
                      value={tempDateRange.toDate}
                      onChange={(e) => setTempDateRange({ ...tempDateRange, toDate: e.target.value })}
                      max={new Date().toISOString().split('T')[0]}
                      min={tempDateRange.fromDate || ''}
                    />
                  </div>
                  <div className="flex gap-2 mt-2">
                    <button
                      onClick={handleApplyDateFilter}
                      disabled={!tempDateRange.fromDate && !tempDateRange.toDate}
                      className="flex-1 bg-[#1F456E] hover:bg-[#163552] text-white text-sm font-medium py-2 px-4 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Apply Filter
                    </button>
                    {(dateRange?.fromDate || dateRange?.toDate) && (
                      <button
                        onClick={handleClearDateRange}
                        className="text-sm text-[#ED7117] hover:text-[#d66315] font-medium px-3"
                      >
                        Clear
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </Popover>
          </>
        )}

        {/* Filter Button */}
        <IconButton
          onClick={handleFilterClick}
          sx={{
            backgroundColor: '#E5E7EB',
            '&:hover': { backgroundColor: '#D1D5DB' },
            width: { xs: 44, sm: 56 },
            height: { xs: 44, sm: 56 },
          }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="w-5 h-5 sm:w-7 sm:h-7"
            viewBox="0 0 24 24"
            fill="none"
          >
            <path
              d="M3.35 2h8.9c.74 0 1.35.61 1.35 1.35v1.48c0 .54-.34 1.21-.67 1.55l-2.9 2.56c-.4.34-.67 1.01-.67 1.55v2.9c0 .4-.27.94-.61 1.15l-.94.61c-.88.54-2.09-.07-2.09-1.15v-3.57c0-.47-.27-1.08-.54-1.42l-2.56-2.7c-.34-.34-.61-.94-.61-1.35V3.41C2 2.61 2.61 2 3.35 2Z"
              stroke="#101010"
              strokeWidth="1.5"
              strokeMiterlimit="10"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M2 12v3c0 5 2 7 7 7h6c5 0 7-2 7-7V9c0-3.12-.78-5.08-2.59-6.1-.51-.29-1.53-.51-2.46-.66M13 13h5M11 17h7"
              stroke="#101010"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </IconButton>

        {/* Filter Menu */}
        <Menu
          anchorEl={filterAnchorEl}
          open={isFilterOpen}
          onClose={handleFilterClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          slotProps={{
            paper: {
              sx: {
                marginTop: '8px',
                borderRadius: '12px',
                boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
                minWidth: '160px',
              }
            }
          }}
        >
          <MenuItem
            onClick={() => handleFilterItemClick("past")}
            sx={{
              fontWeight: (filterNoteType || filterEventType) === "past" ? 'bold' : 'normal',
            }}
          >
            Past
          </MenuItem>
          <MenuItem
            onClick={() => handleFilterItemClick("yesterday")}
            sx={{
              fontWeight: (filterNoteType || filterEventType) === "yesterday" ? 'bold' : 'normal',
            }}
          >
            Yesterday
          </MenuItem>
          <MenuItem
            onClick={() => handleFilterItemClick("today")}
            sx={{
              fontWeight: (filterNoteType || filterEventType) === "today" ? 'bold' : 'normal',
            }}
          >
            Today
          </MenuItem>
          <MenuItem
            onClick={() => handleFilterItemClick("isImp")}
            sx={{
              fontWeight: (filterNoteType || filterEventType) === "isImp" ? 'bold' : 'normal',
            }}
          >
            Is Important
          </MenuItem>
          <MenuItem onClick={handleClearAllFilters}>
            Clear All Filters
          </MenuItem>
        </Menu>
      </div>
    </div>
  );
};

export default SearchBar;
